// src/components/PwaBanners.jsx
// Two small, non-blocking UI pieces: an "Install this app" card (offered
// once per browser session if the browser makes the prompt available and
// the person hasn't already dismissed it or installed), and an "Update
// available" pill that appears only once a new version has actually
// finished downloading in the background — never before it's ready, and
// never applied without the person choosing to.
//
// iOS Safari genuinely never fires beforeinstallprompt at all — that's a
// real platform limitation, not something to work around in code. Without
// separate handling, an iPhone visitor gets no install offer whatsoever,
// silently. Instead: detect iOS specifically and show manual step-by-step
// instructions (Share -> Add to Home Screen) since that's the only path
// that actually exists there.
import { useState, useEffect } from "react";
import { initPwa, promptInstall, applyUpdate } from "@/lib/pwa";

const INSTALL_DISMISSED_KEY = "pwa-install-dismissed";

function isIOS() {
  // Standard iPhone/iPod check, plus the iPadOS quirk where Safari
  // reports as "MacIntel" but still exposes multi-touch — iPadOS 13+
  // deliberately masquerades as desktop Safari otherwise.
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  // Already installed and running from the home screen — don't offer to
  // install something that's already installed. navigator.standalone is
  // iOS-specific; display-mode is the cross-platform standard the
  // Android/Chrome path also implicitly satisfies once installed.
  return window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
}

export default function PwaBanners() {
  const [showInstall, setShowInstall] = useState(false);
  const [showIosInstall, setShowIosInstall] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(
    () => sessionStorage.getItem(INSTALL_DISMISSED_KEY) === "1"
  );

  useEffect(() => {
    initPwa({
      onInstallAvailable: () => setShowInstall(true),
      onUpdateAvailable: () => setShowUpdate(true),
    });
    // iOS never fires the beforeinstallprompt event this hooks into above,
    // so its offer is decided here directly instead — no event to wait
    // for, just "is this an iPhone/iPad, in Safari's browser tab (not
    // already installed), and not already dismissed this session".
    if (isIOS() && !isStandalone()) setShowIosInstall(true);
  }, []);

  const dismissInstall = () => {
    setShowInstall(false);
    setShowIosInstall(false);
    setInstallDismissed(true);
    sessionStorage.setItem(INSTALL_DISMISSED_KEY, "1");
  };

  const doInstall = async () => {
    const accepted = await promptInstall();
    setShowInstall(false);
    if (!accepted) { setInstallDismissed(true); sessionStorage.setItem(INSTALL_DISMISSED_KEY, "1"); }
  };

  // The kiosk display (/grad/display) already runs its own separate,
  // dedicated PWA identity — its own manifest, swapped in on mount (see
  // useKioskManifest in GradKioskDisplay.jsx), meant to be installed as
  // its own focused full-screen app. A generic install/update banner from
  // the main app sitting fixed at the bottom of that screen would risk
  // overlapping the kiosk's own controls — the late-arrival wrap-up
  // section and the Home button both live in exactly that area — during
  // an actual live reading. Simplest, safest fix: this component just
  // doesn't render there at all; the kiosk page can grow its own install/
  // update UI later if that's ever wanted, deliberately positioned to fit
  // its own layout.
  //
  // The public registration forms (/grad/confirm, /grad/guardian) are a
  // different reason for the same conclusion: a student or guardian
  // filling out a one-time attendance/guardian confirmation isn't a
  // repeat visitor of the platform — prompting them to install the whole
  // events platform as an app in the middle of a form they'll likely
  // never open again is a genuine distraction from the one thing they
  // actually came to do, not a helpful offer.
  const path = window.location.pathname;
  if (path.startsWith("/grad/display") || path.startsWith("/grad/confirm") || path.startsWith("/grad/guardian")) return null;

  return (
    <>
      {showUpdate && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] bg-slate-900 text-white rounded-full shadow-lg px-4 py-2.5 flex items-center gap-3 text-sm">
          <span>A new version is available</span>
          <button
            onClick={() => { applyUpdate(); setShowUpdate(false); }}
            className="bg-white text-slate-900 font-semibold rounded-full px-3 py-1 text-xs"
          >
            Update
          </button>
        </div>
      )}

      {showInstall && !installDismissed && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-[90] bg-white border rounded-xl shadow-lg p-4 flex items-start gap-3">
          <img src="/logo192.png" alt="" className="w-10 h-10 rounded-lg shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Install this app</p>
            <p className="text-xs text-muted-foreground mt-0.5">Add it to your home screen for quick, full-screen access — no browser bar, opens instantly.</p>
            <div className="flex gap-2 mt-2">
              <button onClick={doInstall} className="text-xs font-semibold bg-primary hover:bg-primary/90 text-white rounded-md px-3 py-1.5">Install</button>
              <button onClick={dismissInstall} className="text-xs text-muted-foreground hover:text-foreground px-3 py-1.5">Not now</button>
            </div>
          </div>
        </div>
      )}

      {showIosInstall && !installDismissed && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-[90] bg-white border rounded-xl shadow-lg p-4 flex items-start gap-3">
          <img src="/logo192.png" alt="" className="w-10 h-10 rounded-lg shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Install this app</p>
            <p className="text-xs text-muted-foreground mt-1">
              Tap <span className="font-semibold text-foreground">Share</span> <span aria-hidden>⬆️</span> below, then
              <span className="font-semibold text-foreground"> "Add to Home Screen"</span>.
            </p>
            <button onClick={dismissInstall} className="text-xs text-muted-foreground hover:text-foreground mt-2 px-0">Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
